import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import HeroCarousel from '../components/home/HeroCarousel';
import CategoryTiles from '../components/home/CategoryTiles';
import TrustBanner from '../components/home/TrustBanner';
import RestockBadge from '../components/home/RestockBadge';
import UpcomingSets from '../components/home/UpcomingSets';
import FlashSaleStrip from '../components/home/FlashSaleStrip';
import { Spinner, EmptyState } from '../components/ui';
import SEO from '../components/SEO';
import PageRenderer from '../components/PageRenderer';
import './Home.css';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/products?featured=true&limit=8').then(r => r.ok ? r.json() : []),
      fetch('/api/products?sort=newest&limit=8').then(r => r.ok ? r.json() : []),
      fetch('/api/products?sort=trending&limit=8').then(r => r.ok ? r.json() : []).catch(() => []),
    ])
      .then(([feat, fresh, trend]) => {
        setFeaturedProducts(feat);
        setNewProducts(fresh);
        setTrendingProducts(trend);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching products:', err);
        setLoading(false);
      });
  }, []);

  return (
    <div className="home page">
      <SEO
        title="Pokemon TCG singles, sealed & graded — shipped from Canada"
        description="SilverGames is a Canadian-owned Pokemon TCG shop. Daily restocks, +20% buylist bonus in store credit, fast tracked shipping across Canada."
        type="website"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Store',
          name: 'SilverGames',
          url: 'https://silvergames.ca',
          description: 'Canadian Pokemon TCG retailer — singles, sealed, graded slabs.',
          paymentAccepted: 'Credit Card, Apple Pay, Google Pay',
          currenciesAccepted: 'CAD',
          areaServed: { '@type': 'Country', name: 'Canada' },
        }}
      />
      <RestockBadge />
      {/* Page builder takes over the main hero+tiles layout when admin has
          configured sections in /admin/builder. Until they do, the legacy
          hardcoded layout renders unchanged via the `fallback` prop. */}
      <PageRenderer
        slug="home"
        fallback={
          <>
            <HeroCarousel />
            <FlashSaleStrip />
            <TrustBanner />
            <CategoryTiles />
          </>
        }
      />

      {trendingProducts.length > 0 && (
        <section className="featured-section">
          <div className="section-header">
            <h2>Trending now</h2>
            <Link to="/shop?sort=trending" className="section-header__link">See more →</Link>
          </div>
          <div className="grid">
            {trendingProducts.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      <UpcomingSets />

      <section className="featured-section">
        <div className="section-header">
          <h2>Featured Products</h2>
          <Link to="/shop" className="section-header__link">Browse all →</Link>
        </div>
        {loading ? (
          <Spinner label="Loading products…" />
        ) : featuredProducts.length === 0 ? (
          <EmptyState title="No featured products yet"
            description="Check back soon — we restock daily." />
        ) : (
          <div className="grid">
            {featuredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </section>

      {newProducts.length > 0 && (
        <section className="featured-section">
          <div className="section-header">
            <h2>New Arrivals</h2>
            <Link to="/shop?sort=newest" className="section-header__link">Browse all →</Link>
          </div>
          <div className="grid">
            {newProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      )}

      <section className="features">
        <div className="feature-grid">
          <div className="feature-card">
            <h3>Authentic Cards</h3>
            <p>Verified authentic and condition-graded</p>
          </div>
          <div className="feature-card">
            <h3>Free Shipping over $100</h3>
            <p>Tracked, insured, fast across Canada</p>
          </div>
          <div className="feature-card">
            <h3>Sell to Us</h3>
            <p>Instant quotes — +20% bonus in store credit</p>
          </div>
          <div className="feature-card">
            <h3>Easy Returns</h3>
            <p>30-day refund · refund processed in 48h</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
